import { useEffect, useMemo, useState } from "react";
import { ReactComponent as IconErrorHint } from "@/assets/svgs/error-hint.svg";
import useEForm from "@/hooks/use-eform/index";
import { useHealthdex } from "@/hooks/use-healthdex/index.js";
import addFormats from "@/utils/custom-ajv-formats";
import Ajv from "ajv";
import PropTypes from "prop-types";
import CellBooleanRadioComponent from "./cell-boolean-radio.jsx";
import CellBooleanComponent from "./cell-boolean.jsx";
import CellDropdownComponent from "./cell-dropdown.jsx";
import CellFileUploadComponent from "./cell-file-upload.jsx";
import CellMultiDropdownComponent from "./cell-multi-select.jsx";
import CellNumberComponent from "./cell-number.jsx";
import CellStringComponent from "./cell-string.jsx";
import { useSelector } from "react-redux";
import { featureConstant } from '@/constants/configuration/feature.js';

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

const EFormFieldComponent = ({
	fieldId = '',
	isReadOnly,
	schema,
	required,
	payload,
	setPayload,
	isDirty,
	parentProps,
	setIsValidFormat,
	errorListToDisplay,
	isSubmitClicked = false,
}) => {

	const { getFieldValue, getNextPayload } = useEForm();
	const [eFormFieldError, setEFormFieldError] = useState(false);
	const [inputProps, setInputProps] = useState({});
	const [touched, setTouched] = useState(false);

	const isHealthDex = useHealthdex();
	
	const { features } = useSelector(state => state.auth)
	const isErrorEnhancementEnabled = features?.includes(featureConstant.ERROR_MESSAGE_ENHANCEMENT);

	const propertyName = parentProps[parentProps.length - 1];

	const schemaType = schema["x-uitype"] ?? schema.type;
	const currentType = Array.isArray(schemaType)
		? schemaType.find((d) => d !== "null")
		: schemaType;

	// Helper function to check if a field has errors (including nested paths)
	const hasErrorsForField = useMemo(() => {
		// Direct match
		if (Object.keys(errorListToDisplay).includes(fieldId)) {
			return true;
		}

		// Check for nested paths with slashes (e.g., set_dets/0/sample_no)
		if (parentProps && parentProps.length > 0) {
			// For array items, check paths that include array name and index
			if (parentProps.length >= 2) {
				const arrayName = parentProps[parentProps.length - 2];
				const arrayIndex = parentProps[parentProps.length - 1];
				
				// If the last item is a number (array index)
				if (!isNaN(arrayIndex)) {
					const path = `${arrayName}/${arrayIndex}/${fieldId}`;
					if (Object.keys(errorListToDisplay).includes(path)) {
						return true;
					}
				}
			}
		}

		// Look for any path that ends with the current fieldId
		return Object.keys(errorListToDisplay).some(key => {
			const parts = key.split('/');
			return parts[parts.length - 1] === fieldId;
		});
	}, [errorListToDisplay, fieldId, parentProps]);

	const shouldShowErrorCaption = useMemo(
		() =>
			isErrorEnhancementEnabled &&
			hasErrorsForField &&
			(isSubmitClicked || touched),
		[
			errorListToDisplay,
			isSubmitClicked,
			isErrorEnhancementEnabled,
			fieldId,
			touched,
			hasErrorsForField
		]
	);

	useEffect(() => {
		if (isDirty && inputProps?.value !== undefined) {
			validateSchema(inputProps.value);
		}
	});

	function updatePayload(value) {
		if (!touched) {
			setTouched(true);
		}
		const nextPayload = getNextPayload(payload, parentProps, value);
		setPayload(nextPayload);
		return nextPayload;
	}

	function validateSchema(value) {
		if ((isHealthDex && isDirty) || !isHealthDex) {
			const validate = ajv.compile(schema);
			const validateResult = validate(value);
			setEFormFieldError(!validateResult);
			return validateResult;
		}
	}

	function validateErrors(fieldTitle) {
		const fieldError = errorListToDisplay?.find((error) => error.ref === fieldTitle.split(' ').join('_'));
  		return fieldError ? fieldError.message : fieldTitle + " is not valid";
	}

	function getErrorMessage() {
		// Find the appropriate error key for the current field
		const getErrorKey = () => {
			// Direct match
			if (Object.keys(errorListToDisplay).includes(fieldId)) {
				return fieldId;
			}

			// Check for nested paths with slashes (e.g., set_dets/0/sample_no)
			// Build possible paths based on parentProps
			if (parentProps && parentProps.length > 0) {
				// Last element in parentProps is the current field or index
				const possiblePaths = [];
				
				// For array items, the path might include the array name and index
				if (parentProps.length >= 2) {
					const arrayName = parentProps[parentProps.length - 2];
					const arrayIndex = parentProps[parentProps.length - 1];
					
					// If the last item is a number (array index)
					if (!isNaN(arrayIndex)) {
						possiblePaths.push(`${arrayName}/${arrayIndex}/${fieldId}`);
					}
				}

				// Check if any of the possible paths exist in errorListToDisplay
				for (const path of possiblePaths) {
					if (Object.keys(errorListToDisplay).includes(path)) {
						return path;
					}
				}
			}

			// Look for any path that ends with the current fieldId
			const matchingKeys = Object.keys(errorListToDisplay).filter(key => {
				const parts = key.split('/');
				return parts[parts.length - 1] === fieldId;
			});

			if (matchingKeys.length > 0) {
				return matchingKeys[0];
			}

			return null;
		};

		const errorKey = getErrorKey();

		if (!errorKey || !errorListToDisplay[errorKey] || errorListToDisplay[errorKey].length === 0) {
			return schema.title + " is not valid";
		}

		if (!Array.isArray(errorListToDisplay[errorKey])) {
			return schema.title + " is not valid";
		}

		const parts = errorListToDisplay[errorKey][0].split(':');
		if (parts.length >= 2) {
			const errorPart = parts.slice(1).join(':').trim();
			return errorPart;
		}

		return errorListToDisplay[errorKey][0];
	}

	const props = {
		error: (eFormFieldError && !isErrorEnhancementEnabled) || shouldShowErrorCaption,
		isReadOnly,
		schema,
		payload,
		parentProps,
		inputProps,
		setInputProps,
		updatePayload,
		validateSchema,
		getFieldValue,
		setEFormFieldError,
		setIsValidFormat,
		required,
		propertyName
	};

	return (
		<div className="eform-row" data-testid="eform-row">
			<div className="eform-label-container tooltip">
				{!!required?.includes(propertyName) && (
					<span
						className="eform-label-asterisk"
						data-testid="eform-label-asterisk"
					>
						*
					</span>
				)}

				<div className="eform-label" data-testid="eform-label" id={(schema.title).split(' ').join('_')}>
					{schema.title ?? schema.description}
				</div>

				<div className="tooltiptext eform-tooltip-text">
					{schema.title ?? schema.description}
				</div>
			</div>

			{currentType === "string" && <CellStringComponent {...props} />}
			{currentType === "dropdown" && <CellDropdownComponent {...props} />}
			{currentType === "multi-select" && <CellMultiDropdownComponent {...props} />}
			{currentType === "file-upload" && <CellFileUploadComponent {...props} />}
			{(currentType === "number" || currentType === "integer") && <CellNumberComponent {...props} />}

			{!isHealthDex && currentType === "boolean" && <CellBooleanComponent {...props} />}
			{!isHealthDex && currentType === "radio" && <CellBooleanRadioComponent {...props} />}
			{isHealthDex && (currentType === "radio" || currentType === "boolean") && <CellBooleanRadioComponent {...props} />}

			{eFormFieldError && !isErrorEnhancementEnabled && (
				<div className="eform-input-error flex-box flex-align-center">
					<IconErrorHint />
					{!isHealthDex && <div>{schema.title ?? schema.description} is not valid</div>}
					{isHealthDex && <div>{validateErrors(schema.title ?? schema.description)}</div>}
				</div>
			)}
			{shouldShowErrorCaption &&
				<div className="eform-input-error flex-box flex-align-center">
					<IconErrorHint />
					<div>{getErrorMessage()}</div>
				</div>
			}
		</div>
	);
};

EFormFieldComponent.propTypes = {
	schema: PropTypes.shape({
		title: PropTypes.string.isRequired,
		description: PropTypes.string.isRequired,
	}).isRequired,
	errorListToDisplay: PropTypes.oneOfType([
		PropTypes.array,
		PropTypes.object
	]),
}

export default EFormFieldComponent;
